import { io, Socket } from "socket.io-client";
import { accessTokenStore, fn } from "../../../stores";
import { get } from "svelte/store";
import { TSocketEventData } from "../../types";
import { Pages } from "../../enums";
import { TEvmPaymentReturn } from "@packages/utils/types";

const socketClients = new Map<string, Socket>();

const disconnectSocket = async (wssUrl: string) => {
  const socket = socketClients.get(wssUrl);
  socket?.disconnect();
  socketClients.delete(wssUrl);
};

const connectToSocket = async (wssUrl: string): Promise<Socket> => {
  if (socketClients.has(wssUrl)) return socketClients.get(wssUrl) as Socket;

  const socket = io(wssUrl, {
    auth: {
      bearer_token: get(accessTokenStore),
    },
  });

  await new Promise((resolve, reject) => {
    socket.once("connect_error", (err) => reject(err));
    socket.once("connect", () => resolve(socket));
    setTimeout(
      () => reject(new Error("Cannot connect to websocket server")),
      5000
    );
  });

  socketClients.set(wssUrl, socket);
  return socket;
};

const connectSocketForSignatures = async (wssUrl: string) => {
  const socket = await connectToSocket(wssUrl);
  socket.on("passport_id/sign", onSignRequest);
};

export const evmPaymentRequest = async (
  wssUrl: string,
  payload: {
    address: string;
    chain_id: number;
    card_token: string;
    description: string;
    client: string;
  } & (
    | {
        tokens: {
          contract_address: string;
          token_id: number;
          amount: number;
        };
      }
    | {
        memberships: {
          contract_address: string;
        };
      }
  )
) => {
  const socket = await connectToSocket(wssUrl);
  return new Promise((resolve, reject) => {
    socket.emit(
      "memberships" in payload
        ? "contracts/buy_memberships"
        : "contracts/buy_tokens",
      {
        headers: {},
        payload,
      },
      (
        response:
          | { error: { message: string } }
          | {
              payload: TEvmPaymentReturn;
            }
      ) => {
        disconnectSocket(wssUrl);
        if ("error" in response) {
          return reject(response.error);
        }
        return resolve(response.payload);
      }
    );
  });
};

const signDataAndEmitEvent = async (
  wssUrl: string,
  signature: string,
  headers: TSocketEventData["headers"]
) => {
  try {
    if (!socketClients.has(wssUrl))
      throw new Error("Socket client not connected");
    socketClients.get(wssUrl)?.emit(headers.emit_event, {
      headers: headers.emit_event_headers,
      payload: { signature },
    });
  } catch (err) {
    console.error(err);
  }
};
const onSignRequest = async (data: TSocketEventData) => {
  const { openUI } = get(fn);
  openUI(Pages.SIGN, {
    message: data.payload.message,
    socketEventDataHeaders: data.headers,
  });
};
export default {
  connectSocketForSignatures,
  signDataAndEmitEvent,
};
